
import { createContext, useContext, useEffect, useState } from "react";

type ThemePreference = "dark" | "light" | "system";
type ResolvedTheme = "dark" | "light";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: ThemePreference;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: ResolvedTheme;
  preferredTheme: ThemePreference;
  setTheme: (theme: ThemePreference) => void;
  setForcedTheme: (theme: ResolvedTheme | null) => void;
};

const ThemeProviderContext = createContext<ThemeProviderState | undefined>(undefined);

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [preferredTheme, setPreferredTheme] = useState<ThemePreference>(
    () => (localStorage.getItem(storageKey) as ThemePreference) || defaultTheme
  );
  const [forcedTheme, setForcedTheme] = useState<"dark" | "light" | null>(null);
  const [systemTheme, setSystemTheme] = useState<"dark" | "light">(() =>
    window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  );
  const theme: "dark" | "light" = forcedTheme ?? (preferredTheme === "system" ? systemTheme : preferredTheme);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => setSystemTheme(media.matches ? "dark" : "light");
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    root.classList.add(theme);
  }, [theme]);

  const value = {
    theme,
    preferredTheme,
    setTheme: (theme: ThemePreference) => {
      localStorage.setItem(storageKey, theme);
      setPreferredTheme(theme);
    },
    setForcedTheme,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = (): ThemeProviderState => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
